import React, { useEffect, useMemo } from "react";
import { Stack, Typography, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import FormSwitchField from "../components/common/FormSwitchField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  createCurriculumRequirement,
  updateCurriculumRequirement,
} from "../redux/actions/curriculumRequirementActions";
import { getBranches } from "../redux/actions/branchActions";
import { getSubjects } from "../redux/actions/subjectActions";
import { getGradeLevels } from "../redux/actions/gradeLevelActions";
import { getAcademicYears } from "../redux/actions/academicYearActions";
import { curriculumRequirementActions } from "../redux/slices/curriculumRequirement";
import { cleanFormData } from "../helpers/formHelpers";

import { CurriculumRequirementResponseDto } from "../shared/dtos/curriculum/CurriculumRequirementResponseDto";
import { CurriculumRequirementRequestDto } from "../shared/dtos/curriculum/CurriculumRequirementRequestDto";
import { CurriculumRequirementUpdateRequestDto } from "../shared/dtos/curriculum/CurriculumRequirementUpdateRequestDto";

const commonValidation = {
  lessonsPerWeek: Yup.number()
    .required("Lessons per week is required")
    .positive("Must be greater than 0")
    .integer("Must be a whole number"),
  requiresDoubleLesson: Yup.boolean().required(),
};

const createSchema = Yup.object().shape({
  branchId: Yup.string().required("Branch is required"),
  subjectId: Yup.string().required("Subject is required"),
  gradeLevelId: Yup.string().required("Grade level is required"),
  academicYearId: Yup.string().required("Academic year is required"),
  ...commonValidation,
});

const updateSchema = Yup.object().shape({
  ...commonValidation,
});

interface CreateOrUpdateCurriculumRequirementFormProps {
  requirement?: CurriculumRequirementResponseDto | null;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateCurriculumRequirementForm: React.FC<
  CreateOrUpdateCurriculumRequirementFormProps
> = ({
  requirement = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const { items: branches } = useAppSelector((state) => state.branch);
  const { items: subjects } = useAppSelector((state) => state.subject);
  const { items: gradeLevels } = useAppSelector((state) => state.gradeLevel);
  const { items: academicYears } = useAppSelector(
    (state) => state.academicYear,
  );
  const { userInfo } = useAppSelector((state) => state.auth);

  const isSuperAdmin = userInfo?.userRole === "SUPER_ADMIN";

  useEffect(() => {
    if (!isEditing) {
      if (isSuperAdmin && (!branches || branches.length === 0))
        dispatch(getBranches());
      if (!subjects || subjects.length === 0) dispatch(getSubjects());
      if (!gradeLevels || gradeLevels.length === 0) dispatch(getGradeLevels());
      if (!academicYears || academicYears.length === 0)
        dispatch(getAcademicYears());
    }
  }, [
    dispatch,
    isEditing,
    isSuperAdmin,
    branches,
    subjects,
    gradeLevels,
    academicYears,
  ]);

  const branchOptions = useMemo(
    () => branches?.map((b) => ({ value: b.id, label: b.name })) || [],
    [branches],
  );
  const subjectOptions = useMemo(
    () =>
      subjects?.map((s) => ({ value: s.id, label: `${s.name} (${s.code})` })) ||
      [],
    [subjects],
  );
  const gradeOptions = useMemo(
    () => gradeLevels?.map((g) => ({ value: g.id, label: g.name })) || [],
    [gradeLevels],
  );
  const yearOptions = useMemo(
    () => academicYears?.map((y) => ({ value: y.id, label: y.name })) || [],
    [academicYears],
  );

  const initialValues: CurriculumRequirementRequestDto = {
    branchId:
      requirement?.branchId || (isSuperAdmin ? "" : userInfo?.branchId) || "",
    subjectId: requirement?.subjectId || "",
    gradeLevelId: requirement?.gradeLevelId || "",
    academicYearId: requirement?.academicYearId || "",
    lessonsPerWeek: requirement?.lessonsPerWeek || 4,
    requiresDoubleLesson: requirement?.requiresDoubleLesson || false,
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(curriculumRequirementActions.setErrorUpdate(null))
            : () => dispatch(curriculumRequirementActions.setErrorCreate(null))
        }
      />

      <Formik
        initialValues={initialValues}
        validationSchema={isEditing ? updateSchema : createSchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;

          if (isEditing && requirement) {
            const updatePayload: CurriculumRequirementUpdateRequestDto = {
              lessonsPerWeek: Number(rawCleaned.lessonsPerWeek),
              requiresDoubleLesson: Boolean(values.requiresDoubleLesson),
            };
            dispatch(
              updateCurriculumRequirement({
                id: requirement.id,
                values: updatePayload,
              }),
            );
          } else {
            const createPayload: CurriculumRequirementRequestDto = {
              branchId: rawCleaned.branchId,
              subjectId: rawCleaned.subjectId,
              gradeLevelId: rawCleaned.gradeLevelId,
              academicYearId: rawCleaned.academicYearId,
              lessonsPerWeek: Number(rawCleaned.lessonsPerWeek),
              requiresDoubleLesson: Boolean(values.requiresDoubleLesson),
            };
            dispatch(createCurriculumRequirement(createPayload));
          }
        }}
        enableReinitialize
      >
        {({ dirty, isValid }: FormikProps<CurriculumRequirementRequestDto>) => (
          <Form>
            <Stack spacing={2}>
              {!isEditing ? (
                <>
                  {isSuperAdmin && (
                    <FormSelectField
                      name="branchId"
                      label="Branch"
                      options={branchOptions}
                    />
                  )}

                  <FormSelectField
                    name="subjectId"
                    label="Subject"
                    options={subjectOptions}
                  />

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <FormSelectField
                      name="gradeLevelId"
                      label="Grade Level"
                      options={gradeOptions}
                    />
                    <FormSelectField
                      name="academicYearId"
                      label="Academic Year"
                      options={yearOptions}
                    />
                  </Box>
                </>
              ) : (
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 2, textAlign: "left" }}
                >
                  Updating requirements for <b>{requirement?.subjectName}</b> (
                  {requirement?.gradeLevelName}). Structural assignments cannot
                  be modified.
                </Typography>
              )}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <FormTextField
                  name="lessonsPerWeek"
                  label="Lessons Per Week"
                  type="number"
                  slotProps={{ htmlInput: { min: 1 } }}
                />

                <Box
                  sx={{ display: "flex", justifyContent: "flex-start", pl: 1 }}
                >
                  <FormSwitchField
                    name="requiresDoubleLesson"
                    label="Requires Double Lesson"
                  />
                </Box>
              </Box>

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Requirement"}
                loadingText={isEditing ? "Saving..." : "Creating..."}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                sx={{ mt: 1 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateOrUpdateCurriculumRequirementForm;
